/**
 * Recording that a client says they have paid (`record_payment` in
 * `domains/finance/services.py`).
 *
 * NOT verification. This is the first half of the two-person control: it creates a
 * `pending` payment stamped `requested_by` the current session, and stops there. Money
 * is not recognised until someone else confirms it arrived on the payment queue
 * (`PaymentQueuePage`) — this dialog has no verify button and must never grow one, or
 * the same person who registers a payment could also confirm it, which is exactly the
 * control BR-35/T-1 exist to prevent.
 *
 * Only offered once an invoice is issued: `record_payment` refuses against a draft
 * (money arriving before an invoice exists is an unallocated receipt, a different
 * workflow this screen does not attempt).
 */

import { STAFF, qk, useApi, useApiMutation, type Invoice, type Payment } from '@legalcrm/api';
import { Form, FormActions, FormInput, FormSelect, Modal, useZodForm } from '@legalcrm/ui';
import { z } from 'zod';

import { formatMoney } from '@/lib/format';

const METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'gateway', label: 'Gateway' },
];

const schema = z.object({
  amount: z
    .string()
    .trim()
    .min(1, 'Enter an amount')
    .refine((value) => Number(value) > 0, 'Enter an amount greater than zero'),
  method: z.enum(['bank_transfer', 'cash', 'card', 'gateway']),
  reference: z.string(),
});

type Values = z.infer<typeof schema>;

export function RecordPaymentDialog({
  invoice,
  open,
  onClose,
}: {
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const form = useZodForm(schema, {
    defaultValues: {
      amount: Number(invoice.balance) > 0 ? invoice.balance : '',
      method: 'bank_transfer',
      reference: '',
    },
  });

  const record = useApiMutation<Payment, Values>({
    mutationFn: (values) =>
      api.post<Payment>(STAFF.payments, {
        invoice: invoice.id,
        amount: values.amount,
        method: values.method,
        reference: values.reference.trim(),
      }),
    // The payment queue and the invoice's own payment list both change; the invoice
    // record itself does not — a payment is not credited until it is verified.
    invalidates: [qk.all('payments')],
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record a payment"
      description={`Against ${invoice.invoice_number}. This says a client claims to have paid — it does not confirm money has arrived.`}
    >
      <Form
        form={form}
        ariaLabel="Record a payment"
        error={record.error}
        onSubmit={async (values) => {
          await record.mutateAsync(values);
          form.reset();
          onClose();
        }}
      >
        <p className="rounded border bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
          Currently owed: {formatMoney(invoice.balance, invoice.currency)}
        </p>

        <FormInput
          form={form}
          name="amount"
          label={`Amount (${invoice.currency})`}
          inputMode="decimal"
        />

        <FormSelect form={form} name="method" label="Method" options={METHOD_OPTIONS} />

        <FormInput
          form={form}
          name="reference"
          label="Reference"
          hint="Optional. What the client quoted, or a transfer reference."
        />

        <p className="text-xs text-ink-subtle">
          This does not credit the invoice. A second person confirms the money actually
          arrived from the payment queue — whoever records it here cannot also verify it.
        </p>

        <FormActions submitLabel="Record payment" busy={record.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}
